import FirebaseUsage from "../../firebase/firebase.usage";
import {COLLECTIONS} from "../../firebase/constants";
import TaskModel, {TaskSnapshot} from "../../models/responses/task.model";
import {convertIndexToSeconds} from "../cpm-functions/cpm-app/functions/handleEvent";

export const calculateChangeToTasks = async (
    newProjectId: string,
    newDataVersionId: string,
    oldProjectId: string,
    oldDataVersionId: string,
    contractId: string,
    cpmMap: Map<string, any>,
    calendarMap: Map<string, any>,
    currentTasks: Map<string, TaskModel> | null | undefined,
) => {

    const convertTaskToSnapshot = (task: TaskModel): TaskSnapshot | any => {
        const cpmTask = cpmMap.get(task.task_id);
        return {
            taskId: task.task_id,
            projectId: task.projectId,
            dataVersionId: 'na',
            actEndDate: task.act_end_date || task.declaredCompleteTimestamp || null,
            actStartDate: task.act_start_date || null,
            earlyEndDate: FirebaseUsage.timestamp(new Date(convertIndexToSeconds(cpmTask.ef, cpmTask.cal_id, calendarMap) * 1000)),
            earlyStartDate: FirebaseUsage.timestamp(new Date(convertIndexToSeconds(cpmTask.es, cpmTask.cal_id, calendarMap) * 1000)),
            lateEndDate: FirebaseUsage.timestamp(new Date(convertIndexToSeconds(cpmTask.lf, cpmTask.cal_id, calendarMap) * 1000)),
            lateStartDate: FirebaseUsage.timestamp(new Date(convertIndexToSeconds(cpmTask.ls, cpmTask.cal_id, calendarMap) * 1000)),
            targetDuration: task.targetDuration,
            taskName: task.task_name,
            wbs: task.wbs,
            wbsPath: task.wbsPath,
            relations: task.relations,
            status: task.status,
            taskCode: task.task_code,
            index: cpmTask.index || task.index,
        }
    }

    const newProjectTasks = currentTasks ?
        Array.from(currentTasks.values()).map(task => convertTaskToSnapshot(task)) :
        newDataVersionId ?
        await FirebaseUsage.getCompoundQuery(COLLECTIONS.TASK_SNAPSHOTS, [['contractId', '==', contractId],
            ['dataVersionId', '==', newDataVersionId]])
            .then(snapshot =>
                snapshot.docs.map(doc => doc.data() as TaskSnapshot)) :
        await FirebaseUsage.getCompoundQuery(COLLECTIONS.TASK_SNAPSHOTS,
            [['projectId', '==', newProjectId], ['triggeredByUpload', '==', true]])
            .then(snapshot =>
                snapshot.docs.map(doc => doc.data() as TaskSnapshot))

    const oldProjectTasks = oldDataVersionId ?
        await FirebaseUsage.getCompoundQuery(COLLECTIONS.TASK_SNAPSHOTS, [['contractId', '==', contractId],
            ['dataVersionId', '==', oldDataVersionId]])
            .then(snapshot =>
                snapshot.docs.map(doc => doc.data() as TaskSnapshot)) :
        await FirebaseUsage.getCompoundQuery(COLLECTIONS.TASK_SNAPSHOTS,
            [['projectId', '==', oldProjectId], ['triggeredByUpload', '==', true]])
            .then(snapshot =>
                snapshot.docs.map(doc => doc.data() as TaskSnapshot))

    let newTasksMap = new Map();
    let newTasksByTaskId = new Map();

    newProjectTasks.forEach(task => {
        newTasksMap.set(task.taskCode, task)
        newTasksByTaskId.set(task.taskId, task)
    })

    let oldTasksMap = new Map();
    let oldTasksByTaskId = new Map();

    oldProjectTasks.forEach(task => {
        oldTasksMap.set(task.taskCode, task)
        oldTasksByTaskId.set(task.taskId, task)
    })

    let newTasks: any = []
    let deletedTasks: any = []
    let taskDurationChanges: any = []
    let taskNameChanges: any = []
    let taskWBSChanges: any = []
    let tasksDelayed: any = []
    let tasksAhead: any = []
    let logicChanges: any = []
    let tasksStarted: any = []
    let tasksCompleted: any = []

    const statusConverter = {
        'not started': 'not started',
        'block': 'not started',
        'in progress': 'in progress',
        'suspended': 'in progress',
        'completed': 'completed',
        'declared complete': 'completed'
    }

    const newRelationsMap = new Map();
    const oldRelationsMap = new Map();

    newTasksMap.forEach((task: TaskSnapshot, taskCode) => {
        if (!oldTasksMap.has(taskCode)) {
            newTasks.push(task)
        } else {
            const oldTask: TaskSnapshot = oldTasksMap.get(taskCode)
            if (oldTask.targetDuration !== task.targetDuration) {
                taskDurationChanges.push({...oldTask,newDuration: task.targetDuration})
            }
            if (oldTask.taskName !== task.taskName) {
                taskNameChanges.push({...oldTask, newTaskName: task.taskName})
            }
            if (oldTask.wbsPath !== task.wbsPath) {
                taskWBSChanges.push({...oldTask, newWBS: task.wbsPath})
            }
            if (statusConverter[oldTask.status] === 'not started' && statusConverter[task.status] === 'in progress') {
                tasksStarted.push({...oldTask, actStartDate: task.actStartDate})
            }
            if (statusConverter[oldTask.status] !== 'completed' && statusConverter[task.status] === 'completed') {
                tasksCompleted.push({...oldTask, actEndDate: task.actEndDate})
            }
            if (oldTask.earlyEndDate?.seconds > task.earlyEndDate?.seconds) {
                tasksAhead.push({...oldTask, earlyEndNew: task.earlyEndDate})
            }
            if (oldTask.earlyEndDate?.seconds < task.earlyEndDate?.seconds) {
                tasksDelayed.push({...oldTask, earlyEndNew: task.earlyEndDate})
            }
            oldTask.relations.forEach(relation => {
                const oldTask = oldTasksByTaskId.get(relation.task_id)
                const oldPredTask = oldTasksByTaskId.get(relation.pred_task_id)
                if (oldTask && oldPredTask) {
                    const linkId = `${oldTask.taskCode}-${oldPredTask.taskCode}-${relation.pred_type}`
                    oldRelationsMap.set(linkId, {...relation,
                        taskCode: oldTask.taskCode,
                        predTaskCode: oldPredTask.taskCode,
                    })
                }
            });
            task.relations.forEach(relation => {
                const newTask = newTasksByTaskId.get(relation.task_id)
                const newPredTask = newTasksByTaskId.get(relation.pred_task_id)
                if (newTask && newPredTask) {
                    const linkId = `${newTask.taskCode}-${newPredTask.taskCode}-${relation.pred_type}`
                    newRelationsMap.set(linkId, {...relation,
                        taskCode: newTask.taskCode,
                        predTaskCode: newPredTask.taskCode,
                    })
                }
            });
            // for (const link of task.relations) {
            //     const newTaskData = newTasksByTaskId.get(link.task_id)
            //     const newPredTaskData = newTasksByTaskId.get(link.pred_task_id)
            //     if (!newTaskData || !newPredTaskData) {
            //         continue;
            //     }
            //     const linkId = `${newTaskData.taskCode}-${newPredTaskData.taskCode}-${link.pred_type}-${link.lag}`
            //     if (!oldTaskRelations.includes(linkId)) {
            //         debugger;
            //         let minorChange = false;
            //         oldTask.relations.forEach(oldLink => {
            //             const oldTaskData = oldTasksByTaskId.get(oldLink.task_id)
            //             const oldPredTaskData = oldTasksByTaskId.get(oldLink.pred_task_id)
            //             if (oldLink.task_id === link.task_id && oldLink.pred_task_id === link.pred_task_id) {
            //                 if (oldLink.pred_type !== link.pred_type) {
            //                     logicChanges.push({type: "changed type", taskId: link.task_id, predTaskId: link.pred_task_id, oldType: oldLink.pred_type, newType: link.pred_type})
            //                     minorChange = true;
            //                 } else {
            //                     logicChanges.push({type: "changed lag", taskId: link.task_id, predTaskId: link.pred_task_id, oldLag: oldLink.lag, newLag: link.lag})
            //                     minorChange = true;
            //                 }
            //                 if (!minorChange) {
            //                     logicChanges.push({type: "added link", taskId: link.task_id, predTaskId: link.pred_task_id, predType: link.pred_type, lag: link.lag})
            //                 }
            //             }
            //         })
            //     }
            // }
            // for (const link of oldTask.relations) {
            //     const otherTask = oldTasksByTaskId.get(link.task_id === oldTask.taskId ? link.pred_task_id : link.task_id)
            //     if (otherTask && newTasksMap.has(otherTask.task_code)) {
            //         const linkId = `${oldTasksByTaskId.get(link.task_id).task_code}-${oldTasksByTaskId.get(link.pred_task_id).task_code}`
            //         if (!newTaskRelations.includes(linkId)) {
            //             logicChanges.push({
            //                 type: "removed link",
            //                 taskId: newTasksMap.get(link.task_id === oldTask.taskId ? oldTask.taskCode : otherTask.task_code).task_id,
            //                 predTaskId: newTasksMap.get(link.task_id === oldTask.taskId ? otherTask.task_code : oldTask.taskCode).task_id,
            //                 predType: link.pred_type,
            //                 lag: link.lag
            //             })
            //         }
            //     }
            // }
        }
    })

    oldRelationsMap.forEach((relation, linkId) => {
        const newRelation = newRelationsMap.get(linkId)
        if (!newRelation) {
            logicChanges.push({
                type: "removed link",
                ...relation,
            })
        } else if (newRelation.lag !== relation.lag) {
            logicChanges.push({
                type: "changed lag",
                ...relation,
                newLag: newRelation.lag,
            })
        }
    })

    newRelationsMap.forEach((relation, linkId) => {
        const oldRelation = oldRelationsMap.get(linkId)
        if (!oldRelation) {
            logicChanges.push({
                type: "added link",
                ...relation,
            })
        }
    })

    oldTasksMap.forEach((task, taskCode) => {
        if (!newTasksMap.has(taskCode)) {
            deletedTasks.push(task)
        }
    })

    return {
        newTasks: newTasks.sort((a,b) => a.index ? b.index ? a.index - b.index : -1 : b.index ? 0 : a.lateStartDate.seconds - b.lateStartDate.seconds),
        deletedTasks: deletedTasks.sort((a,b) => a.index ? b.index ? a.index - b.index : -1 : b.index ? 1 : a.lateStartDate.seconds - b.lateStartDate.seconds),
        taskDurationChanges: taskDurationChanges.sort((a,b) => (b.newDuration - b.targetDuration) - (a.newDuration - a.targetDuration)),
        taskNameChanges: taskNameChanges.sort((a,b) => a.index ? b.index ? a.index - b.index : -1 : b.index ? 1 : a.lateStartDate.seconds - b.lateStartDate.seconds),
        taskWBSChanges: taskWBSChanges.sort((a,b) => a.index ? b.index ? a.index - b.index : -1 : b.index ? 1 : a.lateStartDate.seconds - b.lateStartDate.seconds),
        tasksDelayed,
        tasksAhead,
        logicChanges,
        tasksStarted: tasksStarted.sort((a,b) => a.actStartDate.seconds - b.actStartDate.seconds),
        tasksCompleted: tasksCompleted.sort((a,b) => a.actEndDate.seconds - b.actEndDate.seconds),
        totalOldTasks: oldProjectTasks.length,
        totalNewTasks: newProjectTasks.length
    }
}